import React, { useState, useEffect } from 'react';
import { Wallet, History, Zap, CheckCircle2, Plus, RefreshCw } from 'lucide-react';
import { agenciesAPI } from '../utils/api';

const AgencyWallet = () => {
  const [stats, setStats] = useState({ coin_balance: 0, total_recharge: 0 });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, historyRes] = await Promise.all([
        agenciesAPI.getDashboardStats(),
        agenciesAPI.getRechargeHistory()
      ]);
      setStats(statsRes.data);
      setHistory(historyRes.data.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching wallet data:', err);
      setLoading(false);
    }
  };

  const handleTopUp = async (e) => {
    e.preventDefault();
    try {
      await agenciesAPI.topUpWallet(topUpAmount);
      setShowTopUpModal(false);
      setTopUpAmount('');
      alert('Wallet Top-Up Successful!');
      fetchData();
    } catch (err) {
      alert('Top-Up Failed');
    }
  };

  if (loading) return <div className="animate-pulse space-y-8">
    <div className="h-48 bg-white/5 rounded-[40px]"></div>
    <div className="h-96 bg-white/5 rounded-[40px]"></div>
  </div>;

  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Wallet Card */}
      <div className="bg-[#0f172a] border border-white/10 rounded-[40px] p-10 relative overflow-hidden group">
         <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-pink-600 to-indigo-600 opacity-10 blur-[80px] group-hover:opacity-20 transition-opacity"></div>
         
         <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 relative z-10">
            <div className="flex items-center gap-8">
               <div className="w-20 h-20 bg-gradient-to-br from-pink-600 to-indigo-600 rounded-[28px] flex items-center justify-center text-white shadow-2xl shadow-pink-600/30">
                  <Wallet size={40} />
               </div>
               <div>
                  <h3 className="text-4xl font-black text-white tracking-tight">{stats.coin_balance?.toLocaleString()} <span className="text-lg text-slate-500 font-bold ml-1">XP</span></h3>
                  <p className="text-slate-500 font-bold uppercase tracking-widest mt-1">Current Wallet Balance</p>
               </div>
            </div>

            <button 
              onClick={() => setShowTopUpModal(true)}
              className="px-8 py-5 bg-pink-600 text-white rounded-3xl font-black text-lg hover:shadow-[0_0_30px_rgba(219,39,119,0.4)] hover:scale-105 transition-all flex items-center justify-center gap-3"
            >
               <Plus size={24} />
               Add Balance
            </button>
         </div>
      </div>

      {/* History Section */}
      <div className="bg-[#0f172a] border border-white/10 rounded-[40px] p-10">
         <div className="flex items-center justify-between mb-10">
            <div>
               <h3 className="text-2xl font-black text-white">Agency Wallet</h3>
               <p className="text-slate-500 text-sm mt-1">Detailed wallet history and transaction logs.</p>
            </div>
            <button onClick={fetchData} className="p-3 bg-white/5 rounded-2xl text-slate-400 hover:text-white transition-all">
               <RefreshCw size={20} />
            </button>
         </div>

         <div className="overflow-x-auto">
            <table className="w-full text-left">
               <thead>
                  <tr className="border-b border-white/5">
                     <th className="pb-6 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] px-4">Transaction ID</th>
                     <th className="pb-6 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] px-4">Recipient</th>
                     <th className="pb-6 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] px-4">Amount</th>
                     <th className="pb-6 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] px-4">Date</th>
                     <th className="pb-6 text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] px-4 text-right">Status</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-white/5">
                  {history.map((item) => (
                    <tr key={item.id} className="group hover:bg-white/[0.02] transition-colors">
                       <td className="py-6 px-4">
                          <span className="text-xs font-mono text-slate-400">#TXN-{item.id.toString().padStart(6, '0')}</span>
                       </td>
                       <td className="py-6 px-4">
                          <div className="flex items-center gap-3">
                             <div className="w-9 h-9 rounded-full bg-indigo-500/10 flex items-center justify-center text-indigo-400 font-black text-xs">
                                {item.user_name?.charAt(0)}
                             </div>
                             <div>
                                <p className="text-sm font-bold text-white">{item.user_name}</p>
                                <p className="text-[10px] text-slate-500 font-bold tracking-widest uppercase">ID: {item.target_user_id}</p>
                             </div>
                          </div>
                       </td>
                       <td className="py-6 px-4">
                          <div className="flex items-center gap-2">
                             <Zap size={14} className="text-pink-500" />
                             <span className="text-sm font-black text-white">{parseFloat(item.amount).toLocaleString()} XP</span>
                          </div>
                       </td>
                       <td className="py-6 px-4">
                          <div className="flex flex-col">
                             <span className="text-sm text-slate-300 font-medium">{new Date(item.created_at).toLocaleDateString()}</span>
                             <span className="text-[10px] text-slate-500 font-bold uppercase">{new Date(item.created_at).toLocaleTimeString()}</span>
                          </div>
                       </td>
                       <td className="py-6 px-4 text-right">
                          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 text-emerald-500 rounded-full">
                             <CheckCircle2 size={12} />
                             <span className="text-[10px] font-black uppercase tracking-widest">Completed</span>
                          </div>
                       </td>
                    </tr>
                  ))}
               </tbody>
            </table>
            {history.length === 0 && (
              <div className="py-20 text-center">
                 <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-600">
                    <History size={40} />
                 </div>
                 <p className="text-slate-600 font-black uppercase tracking-[0.3em]">No Transactions Found</p>
              </div>
            )}
         </div>
      </div>

      {/* Top Up Modal */}
      {showTopUpModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
           <div className="bg-[#0f172a] border border-white/10 rounded-[40px] p-10 max-w-md w-full shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-emerald-600 to-teal-600"></div>
              
              <div className="text-center mb-8">
                 <div className="w-16 h-16 bg-emerald-500/10 rounded-2xl flex items-center justify-center text-emerald-500 mx-auto mb-4">
                    <Wallet size={32} />
                 </div>
                 <h3 className="text-2xl font-black text-white tracking-tight">Add Balance</h3>
                 <p className="text-slate-500 text-sm mt-2">Recharge your agency wallet instantly.</p>
              </div>

              <form onSubmit={handleTopUp} className="space-y-6">
                 <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">Top-Up Amount</label>
                    <input 
                      type="number"
                      required
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white focus:border-emerald-500 outline-none transition-all text-center text-3xl font-black"
                      placeholder="0.00"
                      value={topUpAmount}
                      onChange={(e) => setTopUpAmount(e.target.value)}
                    />
                 </div>

                 <div className="grid grid-cols-3 gap-3">
                    {[5000, 10000, 25000].map(amt => (
                       <button 
                        key={amt} 
                        type="button" 
                        onClick={() => setTopUpAmount(amt.toString())}
                        className="py-3 bg-white/5 rounded-xl text-sm font-bold hover:bg-white/10 transition-all border border-white/5"
                       >
                          +{amt}
                       </button>
                    ))}
                 </div>

                 <div className="flex gap-4">
                    <button 
                      type="button" 
                      onClick={() => setShowTopUpModal(false)}
                      className="flex-1 py-4 bg-white/5 text-slate-400 rounded-2xl font-bold hover:bg-white/10 transition-all"
                    >
                       Cancel
                    </button>
                    <button 
                      type="submit"
                      disabled={!topUpAmount}
                      className="flex-[2] py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-2xl font-bold hover:shadow-[0_0_20px_rgba(16,185,129,0.4)] transition-all"
                    >
                       Confirm Top-Up
                    </button>
                 </div>
              </form>
           </div>
        </div>
      )}
    </div>
  );
};

export default AgencyWallet;
